import { CheckCircle } from "@phosphor-icons/react";
import { AnimatePresence, motion } from "motion/react";
import React, { useState } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { httpService } from "../../../services/httpService";
import { API_CONFIG } from "../../../shared/constants";
import brandLogo from "../../../assets/images/Logo.png";
import {
  MeshBackground,
  useGlobalStyles,
  Field,
  CtaButton,
  IcoMail,
  IcoArrow,
} from "../../AuthShared";
import ThemeToggle from "../../../components/ThemeToggle";

export interface IForgotPasswordResponse {
  message: string;
  uidb64: string;
  token: string;
  reset_url: string;
}

const EASE = [0.22, 1, 0.36, 1];

const ForgotPassword: React.FC = () => {
  useGlobalStyles();
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const validateEmail = (value: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      setError(t("auth.login.emailRequired"));
      return;
    }

    if (!validateEmail(email)) {
      setError(t("auth.invalidEmail"));
      return;
    }

    setIsLoading(true);
    try {
      httpService
        .post<{ message: string }>(API_CONFIG.path.forgotPassword, { email })
        .then(({ message }) => {
          toast.success(message);
          setIsLoading(false);
          setIsSubmitted(true);
        })
        .catch((err: unknown) => {
          setIsLoading(false);
          toast.error(err instanceof Error ? err.message : t("auth.forgotPasswordError"));
        });
    } catch (err: unknown) {
      setIsLoading(false);
      console.log("handleSubmit error:", err);
      toast.error(t("auth.forgotPasswordError"));
    }
  };

  return (
    <div className="fixed inset-0 overflow-hidden flex flex-col text-gray-900 dark:text-gray-100 dark:bg-neutral-950 [font-family:var(--font-geist)]">
      <MeshBackground />

      <div className="absolute top-4 right-4 z-[10]">
        <ThemeToggle />
      </div>

      <div className="relative flex-1 flex items-center justify-center px-4 z-[2]">
        <div className="w-full" style={{ maxWidth: "clamp(360px, 90vw, 460px)" }}>

          {/* Logo */}
          <motion.div
            className="flex justify-center mb-5"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: EASE }}
          >
            <img src={brandLogo} alt="AppRevBooster" className="h-12 object-contain" />
          </motion.div>

          {/* Glass card */}
          <motion.div
            className="relative overflow-hidden p-[24px_24px_22px] sm:p-[28px_28px_26px] bg-white/[0.88] dark:bg-neutral-900/95 backdrop-blur-[28px] saturate-[160%] border border-black/[0.09] dark:border-neutral-700 rounded-2xl shadow-xl"
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.08, ease: EASE }}
          >
            {/* Top accent bar */}
            <div
              className="absolute top-0 left-0 right-0 h-[2px]"
              style={{
                background: "linear-gradient(90deg, transparent, #1a3d7c, transparent)",
                backgroundSize: "200% 100%",
                animation: "arb-glow-trail 4s linear infinite",
              }}
            />

            {/* Animate between form and success state */}
            <AnimatePresence mode="wait">
              {isSubmitted ? (
                <motion.div
                  key="success"
                  className="flex flex-col items-center text-center gap-4 py-2"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.35, ease: EASE }}
                >
                  <motion.div
                    className="w-14 h-14 rounded-full bg-green-50 dark:bg-green-900/20 flex items-center justify-center"
                    initial={{ scale: 0.6, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.4, delay: 0.1, ease: EASE }}
                  >
                    <CheckCircle size={32} weight="duotone" className="text-green-500" />
                  </motion.div>
                  <motion.div
                    className="flex flex-col gap-1"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35, delay: 0.18, ease: EASE }}
                  >
                    <h2 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">
                      {t("auth.forgotPassword")}
                    </h2>
                    <p className="text-[13px] text-gray-500 dark:text-neutral-400 leading-relaxed">
                      {t("auth.resetPasswordEmailSent")}
                    </p>
                  </motion.div>
                  <motion.div
                    className="w-full"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35, delay: 0.26, ease: EASE }}
                  >
                    <Link
                      to="/auth"
                      className="mt-1 w-full py-3.5 px-5 rounded-[10px] text-[14px] font-semibold flex items-center justify-center gap-2 transition-all duration-200 min-h-[49px] bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-[0_2px_14px_rgba(26,61,124,0.28)] hover:shadow-[0_4px_20px_rgba(26,61,124,0.38)] hover:-translate-y-px active:translate-y-0 no-underline"
                    >
                      {t("auth.backToLogin")}
                      <IcoArrow size={16} />
                    </Link>
                  </motion.div>
                </motion.div>
              ) : (
                <motion.div
                  key="form"
                  className="flex flex-col gap-5"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.35, ease: EASE }}
                >
                  <motion.div
                    className="flex flex-col gap-1"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35, delay: 0.12, ease: EASE }}
                  >
                    <h2 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">
                      {t("auth.forgotPassword")}
                    </h2>
                    <p className="text-[13px] text-gray-500 dark:text-neutral-400 leading-relaxed">
                      {t("auth.forgotPasswordDescription")}
                    </p>
                  </motion.div>

                  <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.35, delay: 0.2, ease: EASE }}
                    >
                      <Field
                        label={t("common.email")}
                        value={email}
                        onChange={(v) => {
                          setEmail(v);
                          setError(undefined);
                        }}
                        type="email"
                        autoComplete="email"
                        error={error}
                        leading={<IcoMail size={15} />}
                      />
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.35, delay: 0.28, ease: EASE }}
                    >
                      <CtaButton disabled={isLoading} loading={isLoading}>
                        <span>{t("auth.sendResetPasswordLink")}</span>
                        {!isLoading && <IcoArrow size={16} />}
                      </CtaButton>
                    </motion.div>
                  </form>

                  <motion.div
                    className="flex justify-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3, delay: 0.36, ease: EASE }}
                  >
                    <Link
                      to="/auth"
                      className="text-[12.5px] text-primary-500 no-underline hover:text-primary-700 transition-colors font-medium"
                    >
                      {t("auth.rememberPassword")}
                    </Link>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>

      {/* Bottom status bar */}
      <motion.div
        className="relative flex-shrink-0 z-[4] flex flex-wrap justify-center gap-x-3 gap-y-1 py-3 text-[11px] sm:text-[11.5px] text-gray-400 [font-family:var(--font-mono)] tracking-[0.3px]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.5, ease: EASE }}
      >
        <a href="https://apprevbooster.com/privacy-policy" target="_blank" rel="noreferrer" className="text-inherit no-underline hover:text-gray-600 transition-colors">Privacy</a>
        <span>·</span>
        <a href="https://apprevbooster.com/terms-of-service" target="_blank" rel="noreferrer" className="text-inherit no-underline hover:text-gray-600 transition-colors">Terms</a>
      </motion.div>
    </div>
  );
};

export default ForgotPassword;
